import { caseOf, HTTP, map } from '../../lib'
import { update } from '../../lib/vuex'
import { Paginate } from '../../lib/types/Paginate'
import httpClient from '../../api/httpClient'
import { addingPercentages, removePercentages } from '../../lib/types/Store'

export default {
  state: {
    ui: {
      userId: null,
      userLinks: {},
      userLinksStatus: HTTP.Request.Inactive(),
      linkClicks: [],
      linkClicksStatus: HTTP.Request.Inactive(),
      currentLink: null,
      linksStatus: HTTP.Request.Inactive(),
      calculateClickForm: {
        percent: 0,
      },
      linkClicksCpc: [],
      inOpenPaymentCycle: false,
      linkClicksCpcStatus: HTTP.Request.Inactive(),
      pagination: { ...Paginate.defaultState },
    },
  },

  mutations: {
    update(state, { path, value }) {
      state.ui = update(state.ui, path, value)
    },
  },

  getters: {
    userId: ({ ui: { userId } }) => userId,
    userLinks: ({ ui: { userLinks } }) => userLinks,
    userLinksPerPage: ({
      ui: {
        userLinks,
        pagination: { current },
      },
    }) => userLinks[current] || [],
    userLinksStatus: ({ ui: { userLinksStatus } }) => userLinksStatus,
    linkClicks: ({ ui: { linkClicks } }) => linkClicks,
    linkClicksStatus: ({ ui: { linkClicksStatus } }) => linkClicksStatus,
    currentLink: ({ ui: { currentLink } }) => currentLink,
    calculateClickForm: ({ ui: { calculateClickForm } }) => calculateClickForm,
    linksStatus: ({ ui: { linksStatus } }) => linksStatus,
    linkClicksCpc: ({ ui: { linkClicksCpc } }) => linkClicksCpc,
    linkClicksCpcStatus: ({ ui: { linkClicksCpcStatus } }) => linkClicksCpcStatus,
    inOpenPaymentCycle: ({ ui: { inOpenPaymentCycle } }) => inOpenPaymentCycle,
    pagination: ({ ui: { pagination } }) => pagination,
  },

  actions: {
    updateCpc({ commit, getters }, { key, value }) {
      const linkClicksCpc = getters.linkClicksCpc
      linkClicksCpc[key].user_revenue_cpc_new = value
      commit('update', { path: 'linkClicksCpc', value: linkClicksCpc })
    },

    findLinks({ commit, state, getters }, page = 1) {
      commit('update', { path: 'userLinksStatus', value: HTTP.Request.Pending() })
      const { pagination, userId } = getters
      const params = { user_id: userId, page }
      const url = '/admin/tags'
      Paginate.setCurrentPage({ pagination, page, commit, url, params })
        .then((response) => {
          if (response) {
            const { data, current_page } = response.data
            let value = { [current_page]: data }
            if (current_page !== 1) {
              value = { ...state.ui.userLinks, ...value }
            }
            commit('update', { path: 'userLinks', value })
          }
          commit('update', { path: 'userLinksStatus', value: HTTP.Request.Done() })
        })
        .catch(() => {
          commit('update', { path: 'userLinksStatus', value: HTTP.Request.Failed() })
        })
    },

    showLinks({ commit }, value) {
      commit('update', { path: 'currentLink', value })
      linksModal('show')
      commit('update', { path: 'linksStatus', value: HTTP.Request.Done() })
    },

    hideLinks({ commit }) {
      commit('update', { path: 'currentLink', value: null })
      linksModal('close')
      commit('update', { path: 'linksStatus', value: HTTP.Request.Inactive() })
    },

    saveLink({ commit, getters }) {
      commit('update', { path: 'linksStatus', value: HTTP.Request.Pending() })

      return httpClient
        .post('/admin/tags/' + getters.currentLink.id + '/change-link', {
          link: getters.currentLink.link,
        })
        .then(() => {
          commit('update', { path: 'linksStatus', value: HTTP.Request.Done() })
        })
        .catch((err) => commit('setUserLinks', err))
    },

    setLink({ getters }, { link }) {
      getters.currentLink.link = link
    },

    getLinkClicks({ commit, getters }, value) {
      commit('update', { path: 'linkClicksStatus', value: HTTP.Request.Pending() })
      commit('update', { path: 'currentLink', value })
      linkClicksModal(getters)

      return httpClient
        .get(`/admin/tags/${value.id}/link-clicks`)
        .then((response) => {
          const { percent, link_clicks, in_open_payment_cycle } = response.data
          commit('update', {
            path: 'calculateClickForm.percent',
            value: addingPercentages(percent),
          })
          commit('update', { path: 'inOpenPaymentCycle', value: in_open_payment_cycle })
          commit('update', { path: 'linkClicks', value: link_clicks })
          commit('update', {
            path: 'linkClicksStatus',
            value: HTTP.Request.Done(),
          })
        })
        .catch((value) => {
          commit('update', { path: 'linkClicksStatus', value })
        })
    },

    closeLinkClicks({ commit, getters }) {
      commit('update', { path: 'linkClicksStatus', value: HTTP.Request.Inactive() })
      commit('update', { path: 'currentLink', value: null })
      linkClicksModal(getters)
    },

    calculateLinkClicks({ commit, getters }) {
      commit('update', { path: 'linkClicksStatus', value: HTTP.Request.Pending() })

      return httpClient
        .post('/admin/tags/' + getters.currentLink.id + '/link-clicks/recalculate', {
          ...getters.calculateClickForm,
          percent: removePercentages(getters.calculateClickForm.percent),
        })
        .then((response) => {
          const message = 'Link clicks were successfully moved. User summary tables will be updated in a 1-2 min.'
          commit('update', { path: 'linkClicksStatus', value: HTTP.Request.Done(message) })
          commit('update', { path: 'calculateClickForm.percent', value: addingPercentages(response.data.percent) })
          return commit('update', { path: 'linkClicks', value: response.data.link_clicks })
        })
        .catch((err) => {
          commit('update', { path: 'linkClicksStatus', value: err })
        })
    },

    getLinkClicksCpc({ commit, getters }, value) {
      commit('update', { path: 'linkClicksCpcStatus', value: HTTP.Request.Pending() })
      commit('update', { path: 'currentLink', value })

      linkClicksCpcModal(getters)

      return httpClient
        .get(`/admin/tags/${value.id}/link-clicks-cpc`)
        .then((response) => {
          commit('update', { path: 'linkClicksCpcStatus', value: HTTP.Request.Done() })
          commit('update', { path: 'inOpenPaymentCycle', value: response.data.in_open_payment_cycle })

          const linkClicksCpc = map(response.data.link_clicks_cpc, (linkClicksCpc) => ({
            ...linkClicksCpc,
            user_revenue_cpc_new: Number(Number(linkClicksCpc.user_revenue_cpc).toFixed(0) / 100).toFixed(2),
          }))

          return commit('update', { path: 'linkClicksCpc', value: linkClicksCpc })
        })
        .catch((err) => commit('update', { path: 'linkClicksCpcStatus', value: err }))
    },

    closeLinkClicksCpc({ commit, getters }) {
      commit('update', { path: 'linkClicksCpcStatus', value: HTTP.Request.Inactive() })
      commit('update', { path: 'currentLink', value: null })
      linkClicksCpcModal(getters)
    },

    calculateLinkClicksCpc({ commit, getters }) {
      commit('update', { path: 'linkClicksCpcStatus', value: HTTP.Request.Pending() })
      const linkClicksCpcFormatted = map(getters.linkClicksCpc, (linkClicksCpc) => ({
        ...linkClicksCpc,
        user_revenue_cpc_new: Number(Number(Number(linkClicksCpc.user_revenue_cpc_new).toFixed(2) * 100).toFixed(0)),
      }))
      return httpClient
        .post('/admin/tags/' + getters.currentLink.id + '/link-clicks-cpc/recalculate', {
          link_clicks_cpc: linkClicksCpcFormatted,
        })
        .then((response) => {
          const message = 'Link clicks cpc updated. User summary tables will be updated in a 1-2 min.'
          commit('update', { path: 'linkClicksCpcStatus', value: HTTP.Request.Done(message) })

          const linkClicksCpc = map(response.data.link_clicks_cpc, (linkClicksCpc) => ({
            ...linkClicksCpc,
            user_revenue_cpc_new: Number(Number(linkClicksCpc.user_revenue_cpc).toFixed(0) / 100).toFixed(2),
          }))

          return commit('update', { path: 'linkClicksCpc', value: linkClicksCpc })
        })
        .catch((err) => {
          commit('update', { path: 'linkClicksCpcStatus', value: err })
        })
    },
  },
}

const linkClicksModal = (getters) => {
  const modal = document.getElementById('linkClicksModal'),
    modalBackdrop = document.getElementById('modalBackdrop')

  caseOf(getters.linkClicksStatus, {
    Pending: () => {
      modal.classList.add('show')
      modalBackdrop.classList.add('show')
    },
    Inactive: () => {
      modal.classList.remove('show')
      modalBackdrop.classList.remove('show')
    },
    _: () => ({ ids: [], byId: {} }),
  })
}

const linkClicksCpcModal = (getters) => {
  const modal = document.getElementById('linkClicksCpcModal'),
    modalBackdrop = document.getElementById('modalBackdrop')

  caseOf(getters.linkClicksCpcStatus, {
    Pending: () => {
      modal.classList.add('show')
      modalBackdrop.classList.add('show')
    },
    Inactive: () => {
      modal.classList.remove('show')
      modalBackdrop.classList.remove('show')
    },
    _: () => ({ ids: [], byId: {} }),
  })
}

const linksModal = (action) => {
  const modal = document.getElementById('linksModal'),
    modalBackdrop = document.getElementById('modalBackdrop')

  if (action === 'show') {
    modal.classList.add('show')
    modalBackdrop.classList.add('show')
  } else {
    modal.classList.remove('show')
    modalBackdrop.classList.remove('show')
  }
}
